import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  type ProfileModuleDto,
  ProfileModuleType,
  type ProfileTemplateFieldMode,
} from '@up2stay/up2promo-api-client';
import { ConfirmDialog } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { InlineEditCard } from '@up2stay/up2promo-ui';
import { Label } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { isValidPhoneNumber, PhoneInput } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { GripVertical, Mail, Phone, Plus, Trash2 } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import de from 'react-phone-number-input/locale/de';
import en from 'react-phone-number-input/locale/en';
import type {
  ModuleEntryGovernanceProps,
  TemplateOverrideLabels,
} from '@/core/components/profile/module-entry-governance';
import {
  ModuleEntryListRowBody,
  ModuleEntryLockedHint,
  ModuleEntryOverrideHint,
  ModuleEntryPolicySlot,
} from '@/core/components/profile/module-entry-governance-ui';
import { useDndProfileSensors } from '@/core/hooks/use-dnd-profile-sensors';
import { useLocale } from '@/core/hooks/use-locale';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  lockBodyScrollDuringDrag,
  triggerDragEndHaptic,
  triggerDragOverHaptic,
  triggerDragStartHaptic,
  unlockBodyScrollAfterDrag,
} from '@/core/lib/dnd-haptics';
import {
  isEntryPolicyLocked,
  resolveEntryPolicy,
} from '@/core/lib/profile-module-entry-policies';
import type { MemberRosterRow } from '@/core/lib/profile-template-member-values-csv';
import { useDictionary } from '@/core/providers/dictionary-provider';
import type {
  ContactInfoEntry,
  ContactInfoModuleConfig,
  ContactType,
} from '@/core/types/profile-modules';

export interface EditFormState {
  kind: 'email' | 'phone';
  emailValue: string;
  phoneValue: string;
  label: string;
  valueError: string | null;
}

export function getEmptyFormState(): EditFormState {
  return {
    kind: 'email',
    emailValue: '',
    phoneValue: '',
    label: '',
    valueError: null,
  };
}

export function getFormStateFromEntry(entry: ContactInfoEntry): EditFormState {
  const kind = entry.kind ?? 'email';
  return {
    kind,
    emailValue: kind === 'email' ? (entry.value ?? '') : '',
    phoneValue: kind === 'phone' ? (entry.value ?? '') : '',
    label: entry.label ?? '',
    valueError: null,
  };
}

interface ContactEditFormProps {
  state: EditFormState;
  onChange: (next: EditFormState) => void;
  phoneLabels: Record<string, string>;
}

export function ContactEditForm({
  state,
  onChange,
  phoneLabels,
}: ContactEditFormProps) {
  const handleKindChange = (newKind: 'email' | 'phone') => {
    onChange({
      ...state,
      kind: newKind,
      emailValue: '',
      phoneValue: '',
      valueError: null,
    });
  };

  // Ties each visible <Label> to its field for screen readers (DEV-540).
  const fieldId = useId();

  return (
    <>
      <div className="space-y-2">
        <Label>Art</Label>
        <div
          role="radiogroup"
          aria-label="Art"
          className="grid grid-cols-2 gap-2"
        >
          {(
            [
              { value: 'email' as const, label: 'E-Mail', icon: Mail },
              { value: 'phone' as const, label: 'Telefon', icon: Phone },
            ] as const
          ).map((option) => {
            const selected = state.kind === option.value;
            const OptionIcon = option.icon;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => handleKindChange(option.value)}
                className={cn(
                  'flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm',
                  selected
                    ? 'border-foreground bg-muted text-foreground'
                    : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                )}
              >
                <OptionIcon className="h-4 w-4 shrink-0" />
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
      {state.kind === 'email' ? (
        <div className="space-y-2">
          <Label htmlFor={`${fieldId}-email`}>E-Mail-Adresse</Label>
          <Input
            id={`${fieldId}-email`}
            type="email"
            placeholder="name@beispiel.de"
            value={state.emailValue}
            onChange={(e) =>
              onChange({
                ...state,
                emailValue: e.target.value,
                valueError: null,
              })
            }
            className={cn(state.valueError && 'border-destructive')}
            aria-invalid={!!state.valueError}
          />
          {state.valueError ? (
            <p className="text-sm text-destructive">{state.valueError}</p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor={`${fieldId}-phone`}>Telefonnummer</Label>
          <PhoneInput
            id={`${fieldId}-phone`}
            defaultCountry="DE"
            labels={phoneLabels}
            placeholder="+49 123 4567890"
            value={state.phoneValue || undefined}
            onChange={(v) =>
              onChange({
                ...state,
                phoneValue: v || '',
                valueError: null,
              })
            }
            className={cn(state.valueError && '[&_input]:border-destructive')}
            aria-invalid={!!state.valueError}
          />
          {state.valueError ? (
            <p className="text-sm text-destructive">{state.valueError}</p>
          ) : null}
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor={`${fieldId}-title`}>Titel (optional)</Label>
        <Input
          id={`${fieldId}-title`}
          placeholder="z.B. Geschäftlich, Mobil, Privat"
          value={state.label}
          onChange={(e) => onChange({ ...state, label: e.target.value })}
        />
      </div>
    </>
  );
}
